import dayjs, { Dayjs } from "dayjs";

import { TimePickerInput } from "../../../components/TimePicker";
import { TimeFieldWrapper } from "./AdminPage.styles";

/** What the value is stored and handed back as — `<input type="time">`'s. */
const TIME_VALUE = "HH:mm";

/**
 * An arbitrary date to hang the time off. Only the HH:mm is ever read back,
 * and an ISO-shaped string is what dayjs parses without the extra
 * customParseFormat plugin.
 */
const ANCHOR_DATE = "1970-01-01T";

interface IAdminTimeFieldProps {
  /** Ties the field to its `FilterLabel`. */
  id: string;
  /** `HH:mm`. Blank for unset. */
  value: string;
  onChange: (value: string) => void;
}

/**
 * The time field for the /admin pages, and the counterpart to
 * `AdminDateField` (EN-5146 UI feedback).
 *
 * This one was left as `<input type="time">` when the date fields were
 * converted, so the hospital's notification time still opened Chrome's own
 * dropdown: a blue-highlighted list rendered outside the page. `accent-color`
 * is set on the input and does brand a checkbox or a range thumb, but Chrome
 * does not apply it to that popup, so no stylesheet reaches it.
 *
 * `hideTimezone`, unlike the portal's own use of this picker: the value is
 * local to the HOSPITAL's timezone, which is the field right beside it, so
 * stamping the viewer's own zone on the popup would be actively wrong.
 *
 * No `disabled`, unlike `AdminDateField`: the shared picker does not take one
 * and no caller needs it yet. Add it there rather than faking it here.
 */
export function AdminTimeField({ id, value, onChange }: IAdminTimeFieldProps) {
  const parsed = value ? dayjs(`${ANCHOR_DATE}${value}`) : undefined;

  return (
    <TimeFieldWrapper>
      <TimePickerInput
        id={id}
        hideTimezone
        placeholder="Select a time"
        value={parsed?.isValid() ? parsed : undefined}
        // Clearing hands back null despite the signature, hence the guard.
        onChange={(next: Dayjs | null) =>
          onChange(next?.isValid() ? next.format(TIME_VALUE) : "")
        }
      />
    </TimeFieldWrapper>
  );
}
